import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSubscription } from '@apollo/client';
import { REST_URI } from 'lib/appConfig';
import { SUBSCRIBE_OPEN_LABOR_TICKETS_FOR_CONTROL_BAR } from 'lib/api/queries';
import session from 'lib/api/links/session';
import { getAuthHeaders } from 'lib/api/links/helpers';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import {
  filterProgramEventsByPerson,
  normalizePersonId,
} from 'lib/utils/apmPersonScope';
import {
  actionSetCurrentWorkOrderOp,
  actionSetLaborTickets,
  actionSetEventsLoading,
  actionSetJobCardCount,
  actionSetHasCards,
  actionSetHasUnmatchedCards,
  actionSetHasOpenLaborTickets,
} from 'lib/actions';

const POLL_INTERVAL_MS = 10_000;

/**
 * Independently fetches program events (REST) and open labor tickets (GraphQL)
 * so the ERPMultiJobControlBar knows whether to show "Submit All" or
 * "Select Jobs" — even when the apm-opdash-display MFE is not mounted
 * (e.g. the user is on a different tab after a page refresh).
 *
 * Also dispatches currentJob info to Redux so the header and other selectors
 * work correctly regardless of which tab is active.
 *
 * Returns { hasCards, currentJob, loading, refetch }.
 */
export const useProgramEventDisplayState = () => {
  const dispatch = useDispatch();
  const enabled = useSelector(getIsApmERPJobFlow);
  const machine = useSelector(getMachine);
  const scopeStart = useSelector(getScopeStart);
  const latestOperator = useSelector(getLatestOperator);
  const personErpId = normalizePersonId(latestOperator?.erpId);
  const machineRef = machine?.machineRef;
  const erpResourceId = machine?.erpResourceId;

  // ---- Program events (REST poll) ----
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    if (!enabled || !machineRef || !scopeStart || !personErpId) {
      setEvents([]);
      setEventsLoading(false);
      dispatch(actionSetEventsLoading(false));
      return;
    }
    const key = session.getApiKey();
    if (!key) {
      setEventsLoading(false);
      dispatch(actionSetEventsLoading(false));
      return;
    }

    const params = new URLSearchParams({ start: scopeStart });
    const url = `${REST_URI}/operator-view/labor-ticket/${machineRef}/program-events/grouped?${params}`;
    try {
      const response = await fetch(url, { headers: getAuthHeaders(key) });
      if (response.ok) {
        const data = await response.json();
        setEvents(filterProgramEventsByPerson(data || [], personErpId));
      }
    } catch (_err) {
      // non-fatal — will retry on next poll
    } finally {
      setEventsLoading(false);
      dispatch(actionSetEventsLoading(false));
    }
  }, [enabled, machineRef, scopeStart, personErpId, dispatch]);

  useEffect(() => {
    if (!enabled) {
      setEvents([]);
      setEventsLoading(false);
      dispatch(actionSetEventsLoading(false));
      return undefined;
    }
    if (!machineRef || !scopeStart || !personErpId) {
      setEvents([]);
      setEventsLoading(false);
      dispatch(actionSetEventsLoading(false));
      return undefined;
    }
    setEventsLoading(true);
    dispatch(actionSetEventsLoading(true));
    fetchEvents();
    const interval = setInterval(fetchEvents, POLL_INTERVAL_MS);
    const refreshHandler = () => {
      fetchEvents();
    };
    window.addEventListener('apm:opdash-refresh', refreshHandler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('apm:opdash-refresh', refreshHandler);
    };
  }, [enabled, machineRef, scopeStart, personErpId, fetchEvents, dispatch]);

  // ---- Open labor tickets (GraphQL subscription) ----
  const ltWhere = useMemo(() => {
    const conditions = [
      { machineRef: { _eq: machineRef } },
      { clockOut: { _is_null: true } },
    ];
    if (personErpId) {
      conditions.push({ personId: { _eq: personErpId } });
    }
    return { _and: conditions };
  }, [machineRef, personErpId]);

  const { data: ltData, loading: ltLoading } = useSubscription(
    SUBSCRIBE_OPEN_LABOR_TICKETS_FOR_CONTROL_BAR,
    {
      variables: { where: ltWhere },
      skip: !enabled || !machineRef || !personErpId,
    }
  );

  const openLaborTickets = useMemo(() => {
    return ltData?.erpLaborTickets ?? [];
  }, [ltData]);

  // ---- Derived state ----
  const {
    hasCards,
    hasUnmatchedCards,
    hasOpenLaborTickets,
    currentJob,
    cardCount,
  } = useMemo(() => {
    const openLtRefs = new Set(
      openLaborTickets.map((lt) => {
        return lt.laborTicketRef;
      })
    );

    // Visible events: exclude confirmed events whose LT is no longer open
    const visibleEvents = events.filter((event) => {
      if (event.confirmedAt && event.laborTicketRef) {
        return openLtRefs.has(event.laborTicketRef);
      }
      return true;
    });

    const visibleEventWooRefs = new Set(
      visibleEvents
        .filter((e) => {
          return e.workOrderOperationRef != null;
        })
        .map((e) => {
          return Number(e.workOrderOperationRef);
        })
    );

    // Standalone open LTs not covered by an event
    const standaloneLts = openLaborTickets.filter((lt) => {
      return (
        lt.workOrderOperation?.workOrderOperationRef != null &&
        !visibleEventWooRefs.has(lt.workOrderOperation.workOrderOperationRef)
      );
    });

    const matchedEvents = visibleEvents.filter((e) => {
      return e.workOrderOperationRef != null;
    });

    const cards = matchedEvents.length > 0 || standaloneLts.length > 0;
    const unmatchedCards = visibleEvents.some((e) => {
      return e.workOrderOperationRef == null;
    });
    const count = matchedEvents.length + standaloneLts.length;

    let job = null;
    if (visibleEvents.length > 0) {
      const activeEvent = visibleEvents[visibleEvents.length - 1];
      if (activeEvent.workOrderOperationRef != null) {
        const partOp = activeEvent.candidatePartOps?.[0];
        job = {
          workOrderOperationRef: activeEvent.workOrderOperationRef,
          workOrderId: activeEvent.workOrderId ?? null,
          partNumber: activeEvent.partNumber ?? partOp?.partNumber ?? null,
          sequenceNumber:
            activeEvent.sequenceNumber ?? partOp?.sequenceNumber ?? null,
          type: 'production',
          clockIn: activeEvent.eventTime,
          displayState: activeEvent.confirmedAt ? 'CONFIRMED' : 'SUGGESTED',
        };
      } else if (standaloneLts.length > 0) {
        const lt = standaloneLts[0];
        job = {
          workOrderOperationRef:
            lt.workOrderOperation?.workOrderOperationRef ?? null,
          workOrderId: lt.workOrderId,
          partNumber: lt.workOrderOperation?.workOrder?.partNumber ?? null,
          sequenceNumber: lt.sequenceNumber,
          type: lt.type || 'production',
          clockIn: lt.clockIn,
          displayState: 'WAITING_FOR_PROGRAM',
        };
      }
    } else if (standaloneLts.length > 0) {
      const lt = standaloneLts[0];
      job = {
        workOrderOperationRef:
          lt.workOrderOperation?.workOrderOperationRef ?? null,
        workOrderId: lt.workOrderId,
        partNumber: lt.workOrderOperation?.workOrder?.partNumber ?? null,
        sequenceNumber: lt.sequenceNumber,
        type: lt.type || 'production',
        clockIn: lt.clockIn,
        displayState: 'WAITING_FOR_PROGRAM',
      };
    }

    return {
      hasCards: cards,
      hasUnmatchedCards: unmatchedCards,
      hasOpenLaborTickets: openLaborTickets.length > 0,
      currentJob: job,
      cardCount: count,
    };
  }, [events, openLaborTickets]);

  // ---- Redux dispatches (only when values actually change) ----
  const loading = eventsLoading || ltLoading;
  const prevJobKey = useRef('');

  useEffect(() => {
    if (!enabled) return;
    if (loading) return;

    const key = JSON.stringify(currentJob);
    if (key === prevJobKey.current) return;
    prevJobKey.current = key;

    if (currentJob) {
      dispatch(
        actionSetCurrentWorkOrderOp({
          workOrderOperationRef: currentJob.workOrderOperationRef,
          workOrderId: currentJob.workOrderId,
          sequenceNumber: currentJob.sequenceNumber,
          workOrder: { partNumber: currentJob.partNumber },
        })
      );
      // Set a synthetic labor ticket so getJobStatus / getHasOpenJob work
      dispatch(
        actionSetLaborTickets([
          {
            laborTicketRef: null,
            state: 'OPEN',
            type: currentJob.type || 'production',
            clockIn: currentJob.clockIn,
            clockOut: null,
            workOrderId: currentJob.workOrderId,
            sequenceNumber: currentJob.sequenceNumber,
            resourceId: erpResourceId,
            workOrderOperation: {
              workOrderOperationRef: currentJob.workOrderOperationRef,
            },
          },
        ])
      );
    } else {
      dispatch(actionSetCurrentWorkOrderOp(null));
      dispatch(actionSetLaborTickets([]));
    }
  }, [enabled, currentJob, loading, dispatch, erpResourceId]);

  useEffect(() => {
    if (!enabled) return;
    if (!loading) {
      dispatch(actionSetJobCardCount(cardCount));
      dispatch(actionSetHasCards(hasCards));
      dispatch(actionSetHasUnmatchedCards(hasUnmatchedCards));
      dispatch(actionSetHasOpenLaborTickets(hasOpenLaborTickets));
    }
  }, [
    enabled,
    cardCount,
    hasCards,
    hasUnmatchedCards,
    hasOpenLaborTickets,
    loading,
    dispatch,
  ]);

  return { refetch: fetchEvents };
};
